import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import '../styles/Leagues.css';

function LeagueDetail() {
  const { id } = useParams();
  const { token, user, logout } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const scoringModel = sportConfig?.scoring?.model || 'rounds-won';
  const [league, setLeague] = useState(null);
  const [draftPicks, setDraftPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [expandedManager, setExpandedManager] = useState(null);
  const [standings, setStandings] = useState([]);
  const [playerResults, setPlayerResults] = useState({});
  const [scoringTable, setScoringTable] = useState(null);
  const [scoringRounds, setScoringRounds] = useState([]);
  const [resultLoading, setResultLoading] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [snakeDraftState, setSnakeDraftState] = useState(null);
  const [leagueHeroPicks, setLeagueHeroPicks] = useState([]);

  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchLeague();
    fetchDraftPicks();
    fetchStandings();
    fetchScoringTable();
    fetchResults();
    fetchHeroPicks();
  }, [id]);

  // Fetch snake draft state whenever the league loads (only relevant for snake drafts)
  useEffect(() => {
    if (!league || league.draftType !== 'snake' || league.draftStatus === 'pre-draft') return;
    const fetchSnakeState = async () => {
      try {
        const res = await fetch(`${API_URL}/snake/${id}/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) setSnakeDraftState(data.state);
      } catch { /* non-fatal */ }
    };
    fetchSnakeState();
    const interval = setInterval(fetchSnakeState, 7000);
    return () => clearInterval(interval);
  }, [league, id, token, API_URL]);

  const fetchLeague = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/leagues/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLeague(data.league);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to load league');
    } finally {
      setLoading(false);
    }
  };

  const fetchDraftPicks = async () => {
    try {
      const res = await fetch(`${API_URL}/draft/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setDraftPicks(data.picks);
      }
    } catch (err) {
      // draft picks will just be empty
    }
  };

  const fetchHeroPicks = async () => {
    try {
      const res = await fetch(`${API_URL}/heroes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setLeagueHeroPicks(data.heroPicks || []);
    } catch (err) { /* non-fatal */ }
  };

  const fetchStandings = async () => {
    try {
      const res = await fetch(`${API_URL}/scores/${id}/standings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setStandings(data.standings);
      }
    } catch (err) {
      // standings will just be empty
    }
  };

  const fetchScoringTable = async () => {
    try {
      const res = await fetch(`${API_URL}/scores/${id}/table`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setScoringTable(data.scoringTable);
        setScoringRounds(data.rounds);
      }
    } catch (err) {
      // scoring table will use defaults
    }
  };

  const fetchResults = async () => {
    try {
      const res = await fetch(`${API_URL}/scores/${id}/results`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const map = {};
        for (const r of data.results) {
          // Store whichever result type is relevant for this sport
          map[r.player._id] = {
            roundsWon:      r.roundsWon      || [],
            roundsLost:     r.roundsLost     || [],
            roundPositions: r.roundPositions || [],
          };
        }
        setPlayerResults(map);
      }
    } catch (err) {
      // results will just be empty
    }
  };

  // Toggle a round result (rounds-won model): blank → Win → Loss → blank
  const handleToggleResult = async (playerId, round) => {
    const key = `${playerId}-${round}`;
    setResultLoading(prev => ({ ...prev, [key]: true }));

    const currentResult = playerResults[playerId] || {};
    const isWon  = (currentResult.roundsWon  || []).includes(round);
    const isLost = (currentResult.roundsLost || []).includes(round);

    // Cycle: blank → win → loss → blank
    const method  = isLost  ? 'DELETE' : 'POST';
    const outcome = isWon   ? 'loss'   : 'win';

    try {
      const res = await fetch(`${API_URL}/scores/${id}/result`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(method === 'DELETE' ? { playerId, round } : { playerId, round, outcome }),
      });
      const data = await res.json();
      if (data.success) {
        setPlayerResults(prev => ({
          ...prev,
          [playerId]: {
            ...(prev[playerId] || {}),
            roundsWon:  data.result.roundsWon  || [],
            roundsLost: data.result.roundsLost || [],
          },
        }));
        fetchStandings();
      }
    } catch (err) {
      // ignore
    } finally {
      setResultLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Set a round position (position-by-round model)
  const handleSetPosition = async (playerId, round, position) => {
    const key = `${playerId}-${round}`;
    setResultLoading(prev => ({ ...prev, [key]: true }));
    try {
      const method = position === '' ? 'DELETE' : 'POST';
      const body = method === 'DELETE'
        ? { playerId, round }
        : { playerId, round, position: Number(position) };
      const res = await fetch(`${API_URL}/scores/${id}/result`, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setPlayerResults(prev => ({
          ...prev,
          [playerId]: {
            ...(prev[playerId] || {}),
            roundPositions: data.result.roundPositions || [],
          },
        }));
        fetchStandings();
      }
    } catch (err) {
      // ignore
    } finally {
      setResultLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleRemoveManager = async (managerId) => {
    if (!window.confirm('Remove this manager from the league?')) return;
    try {
      const res = await fetch(`${API_URL}/leagues/${id}/managers/${managerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLeague(data.league);
      }
    } catch (err) {
      // ignore
    }
  };

  const handlePromoteManager = async (managerId) => {
    if (!window.confirm('Promote this manager to Commissioner? This is permanent — they will have full commissioner access and cannot be demoted or removed.')) return;
    try {
      const res = await fetch(`${API_URL}/leagues/${id}/managers/${managerId}/promote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLeague(data.league);
      }
    } catch (err) {
      // ignore
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(league.joinCode);
  };

  const handleDeleteLeague = async () => {
    try {
      const res = await fetch(`${API_URL}/leagues/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        navigate('/leagues');
      } else {
        setDeleteError(data.message);
      }
    } catch (err) {
      setDeleteError('Failed to delete league');
    }
  };

  const handleDraftStatusChange = async (newStatus) => {
    try {
      const res = await fetch(`${API_URL}/leagues/${id}/draft-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ draftStatus: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setLeague(data.league);
      }
    } catch (err) {
      // ignore
    }
  };

  const handleUndoPick = async (pickId) => {
    if (!window.confirm('Undo this draft pick?')) return;
    try {
      const res = await fetch(`${API_URL}/draft/${id}/${pickId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        fetchDraftPicks();
      }
    } catch (err) {
      // ignore
    }
  };

  const handleStartFreeAgency = async () => {
    if (!window.confirm('Open Free Agency? All managers will be able to add/drop players (including heroes) until you end it.')) return;
    try {
      const res = await fetch(`${API_URL}/free-agency/${id}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setLeague(prev => ({ ...prev, freeAgencyOpen: true }));
    } catch (err) {
      // ignore
    }
  };

  const handleEndFreeAgency = async () => {
    if (!window.confirm('End Free Agency? Managers will no longer be able to add or drop players.')) return;
    try {
      const res = await fetch(`${API_URL}/free-agency/${id}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLeague(prev => ({ ...prev, freeAgencyOpen: false }));
        fetchDraftPicks();
        fetchHeroPicks();
      }
    } catch (err) {
      // ignore
    }
  };

  const getManagerStats = (managerId) => {
    const picks = draftPicks.filter(p => p.manager._id === managerId);
    const heroPick = leagueHeroPicks.find(hp => hp.manager._id === managerId) || null;
    const spent = picks.reduce((sum, p) => sum + p.costCredits, 0);
    const budget = league?.draftBudget || 175;
    return { picks, heroPick, spent, remaining: budget - spent };
  };

  if (loading) return <div className="leagues-container"><p className="status-msg">Loading...</p></div>;
  if (error) return <div className="leagues-container"><p className="status-msg error">{error}</p></div>;
  if (!league) return null;

  const isCommissioner = league.commissioner._id === user?._id
    || (league.coCommissioners || []).some(c => c._id === user?._id);

  return (
    <div className="leagues-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to="/leagues" className="btn btn-secondary">Back to Leagues</Link>
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <button onClick={logout} className="btn btn-logout">Logout</button>
        </div>
      </nav>

      <div className="leagues-content">
        <header className="league-detail-header">
          <div>
            <h2>{league.name}</h2>
            {isCommissioner && <span className="commissioner-badge">You are the Commissioner</span>}
          </div>
          <div className="league-meta">
            <span>{sportConfig?.appEmoji || '🏆'} {league.tournament?.name || 'No tournament'}</span>
            <span>📋 Status: <strong>{league.draftStatus}</strong></span>
            <span>� Draft type: <strong>{league.draftType === 'snake' ? 'Snake' : 'Open'}</strong></span>
            <span>�💰 Budget: {league.draftBudget || 175} credits per manager</span>
            <span>👥 {league.managers.length} / {league.maxManagers} managers</span>
          </div>
        </header>

        {/* Free Agency banner — visible to everyone while the window is open */}
        {league.freeAgencyOpen && (
          <div className="free-agency-banner">
            <span>🔓 <strong>Free Agency is open</strong> — managers can add/drop players (including heroes) until the commissioner ends it.</span>
            <Link to={`/leagues/${id}/free-agency`} className="btn btn-small btn-primary">Go to Free Agency</Link>
          </div>
        )}

        {/* Tournament Info */}
        {league.tournament && (
          <div className="tournament-info-section">
            <h3>🏆 Tournament Details</h3>
            <div className="tournament-details">
              <span>📍 {league.tournament.location}</span>
              <span>🏟️ {league.tournament.surface}</span>
              <span>📅 {new Date(league.tournament.startDate).toLocaleDateString()} — {new Date(league.tournament.endDate).toLocaleDateString()}</span>
              <span>🏷️ {league.tournament.category}</span>
            </div>
          </div>
        )}

        {/* Commissioner: Join Code Section */}
        {isCommissioner && (
          <div className="join-code-section">
            <h3>🔑 League Join Code</h3>
            <p>Share the code or the invite link with players so they can join your league.</p>
            <div className="code-display">
              {showCode ? (
                <>
                  <span className="code-value">{league.joinCode}</span>
                  <button className="btn btn-small" onClick={copyCode}>Copy Code</button>
                  <button className="btn btn-small btn-cancel" onClick={() => setShowCode(false)}>Hide</button>
                </>
              ) : (
                <button className="btn btn-primary" onClick={() => setShowCode(true)}>Reveal Code</button>
              )}
            </div>
            {showCode && (
              <div className="join-link-row">
                <span className="join-link-label">Invite link:</span>
                <span className="join-link-url">{`${window.location.origin}/join/${league.joinCode}`}</span>
                <button
                  className="btn btn-small"
                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/join/${league.joinCode}`)}
                >
                  Copy Link
                </button>
              </div>
            )}
          </div>
        )}

        {/* Snake Draft Status — visible to all members when a snake draft is active */}
        {league.draftType === 'snake' && league.draftStatus === 'drafting' && snakeDraftState && (
          <div className="snake-status-section">
            <h3>🐍 Snake Draft Status</h3>
            <div className="snake-status-body">
              <div className="snake-status-current">
                <span className="snake-status-label">Now picking</span>
                <span className="snake-status-drafter">
                  {(() => {
                    const d = snakeDraftState.draftOrder?.[snakeDraftState.currentTurnIndex];
                    const name = d?.firstName || d?.username || '—';
                    const isMe = d?._id === user?._id;
                    return isMe ? `⭐ ${name} (You!)` : name;
                  })()}
                </span>
                <span className="snake-status-meta">
                  Round {snakeDraftState.round}
                  {snakeDraftState.paused && ' · ⏸ Paused'}
                </span>
              </div>
              <ol className="snake-status-order">
                {snakeDraftState.draftOrder?.map((m, idx) => {
                  const isCurrent = idx === snakeDraftState.currentTurnIndex;
                  const isMe = m._id === user?._id;
                  return (
                    <li key={m._id} className={`snake-order-item${isCurrent ? ' snake-order-item--active' : ''}`}>
                      <span className="snake-order-name">
                        {m.firstName || m.username}{isMe ? ' (You)' : ''}
                      </span>
                      {isCurrent && <span className="picking-badge">picking</span>}
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}

        {/* Commissioner: Draft Controls */}
        {isCommissioner && (
          <div className="draft-controls-section">
            <h3>🎯 Draft Controls</h3>
            <div className="draft-status-buttons">
              {league.draftType === 'snake' && league.draftStatus === 'drafting' && (
                <Link to={`/leagues/${id}/snake-draft`} className="btn btn-primary">
                  🐍 Go to Snake Draft
                </Link>
              )}
              {league.draftType !== 'snake' && league.draftStatus === 'drafting' && (
                <Link to={`/leagues/${id}/draft`} className="btn btn-primary">
                  🎾 Go to Draft
                </Link>
              )}
              {league.draftStatus === 'pre-draft' && (
                <>
                  {league.draftType === 'snake' ? (
                    <Link to={`/leagues/${id}/snake-draft`} className="btn btn-primary">
                      🐍 Set Up Snake Draft →
                    </Link>
                  ) : (
                    <>
                      <button className="btn btn-primary" onClick={() => handleDraftStatusChange('drafting')}>
                        ▶️ Open Draft
                      </button>
                      <p className="draft-open-warning">
                        ⚠️ Once you open the draft, this league cannot be deleted.
                      </p>
                    </>
                  )}
                  <button
                    className="btn btn-danger"
                    onClick={() => { setDeleteError(null); setShowDeleteConfirm(true); }}
                  >
                    🗑️ Delete League
                  </button>
                </>
              )}
              {league.draftStatus === 'drafting' && (
                <button className="btn btn-accent" onClick={() => handleDraftStatusChange('complete')}>
                  🏁 Close Draft
                </button>
              )}
              {league.draftStatus === 'complete' && (
                <button className="btn btn-secondary-outline" onClick={() => handleDraftStatusChange('drafting')}>
                  🔄 Reopen Draft
                </button>
              )}
              {league.draftStatus === 'complete' && !league.freeAgencyOpen && (
                <button className="btn btn-accent" onClick={handleStartFreeAgency}>
                  🔓 Start Free Agency
                </button>
              )}
              {league.draftStatus === 'complete' && league.freeAgencyOpen && (
                <>
                  <Link to={`/leagues/${id}/free-agency`} className="btn btn-primary">
                    🔓 Go to Free Agency
                  </Link>
                  <button className="btn btn-danger" onClick={handleEndFreeAgency}>
                    🔒 End Free Agency
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Non-commissioner: Draft Entry Button */}
        {!isCommissioner && league.draftStatus === 'drafting' && (
          <div className="draft-controls-section">
            {league.draftType === 'snake' ? (
              <Link to={`/leagues/${id}/snake-draft`} className="btn btn-primary">
                🐍 Go to Snake Draft
              </Link>
            ) : (
              <Link to={`/leagues/${id}/draft`} className="btn btn-primary">
                🎾 Go to Draft
              </Link>
            )}
          </div>
        )}

        {/* Non-commissioner: Free Agency Entry Button */}
        {!isCommissioner && league.draftStatus === 'complete' && league.freeAgencyOpen && (
          <div className="draft-controls-section">
            <Link to={`/leagues/${id}/free-agency`} className="btn btn-primary">
              🔓 Go to Free Agency
            </Link>
          </div>
        )}

        {/* Managers List with Draft Info */}
        <div className="managers-section">
          <h3>👥 Managers</h3>
          <div className="managers-list">
            {league.managers.map(member => {
              const stats = getManagerStats(member._id);
              const isMe = member._id === user?._id;
              const isExpanded = expandedManager === member._id;
              const isManagerCommissioner = member._id === league.commissioner._id
                || (league.coCommissioners || []).some(c => c._id === member._id);

              return (
                <div key={member._id} className="manager-card">
                  <div className="manager-row">
                    <div className="manager-info">
                      <span className="manager-name">
                        {member.firstName || member.username}
                        {isMe && <span className="you-badge">You</span>}
                      </span>
                      {isManagerCommissioner && <span className="role-badge">Commissioner</span>}
                    </div>
                    <div className="manager-draft-stats">
                      <span className="draft-stat">🎯 {stats.picks.length + (stats.heroPick ? 1 : 0)} players</span>
                      <span className="draft-stat">💰 {stats.remaining} cr left</span>
                      {(stats.picks.length > 0 || stats.heroPick) && (
                        <button
                          className="btn btn-small btn-secondary-outline"
                          onClick={() => setExpandedManager(isExpanded ? null : member._id)}
                        >
                          {isExpanded ? 'Hide' : 'View'} Team
                        </button>
                      )}
                    </div>
                    <div className="manager-actions">
                      {isMe && league.draftStatus === 'drafting' && (
                        <Link to={`/leagues/${id}/draft`} className="btn btn-small btn-primary">
                          Draft Players
                        </Link>
                      )}
                      {isCommissioner && !isManagerCommissioner && (
                        <button
                          className="btn btn-small btn-secondary-outline"
                          onClick={() => handlePromoteManager(member._id)}
                        >
                          Promote to Commissioner
                        </button>
                      )}
                      {isCommissioner && !isManagerCommissioner && (
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleRemoveManager(member._id)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  {isExpanded && (stats.picks.length > 0 || stats.heroPick) && (
                    <div className="manager-team">
                      <table className="team-table">
                        <thead>
                          <tr>
                            <th>Pick</th>
                            <th>Player</th>
                            <th>Tour</th>
                            <th>Rank</th>
                            <th>Cost</th>
                            {isCommissioner && <th></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Hero pick row */}
                          {stats.heroPick && (
                            <tr className="hero-pick-row">
                              <td><span className="hero-badge-inline">⚡</span></td>
                              <td className="player-name">
                                <span>{stats.heroPick.player?.name}</span>
                                <span className="hero-free-badge">HERO</span>
                              </td>
                              <td><span className={`tour-badge ${stats.heroPick.player?.tour?.toLowerCase()}`}>{stats.heroPick.player?.tour}</span></td>
                              <td>#{stats.heroPick.player?.ranking}</td>
                              <td>0 cr</td>
                              {isCommissioner && <td></td>}
                            </tr>
                          )}
                          {[...stats.picks]
                            .sort((a, b) => {
                              const aOut = (playerResults[a.player._id]?.roundsLost || []).length > 0;
                              const bOut = (playerResults[b.player._id]?.roundsLost || []).length > 0;
                              if (aOut !== bOut) return aOut ? 1 : -1;
                              return a.pickNumber - b.pickNumber;
                            })
                            .map(pick => {
                              const isEliminated = (playerResults[pick.player._id]?.roundsLost || []).length > 0;
                              return (
                                <tr key={pick._id} className={isEliminated ? 'player-eliminated-row' : ''}>
                                  <td>#{pick.pickNumber}</td>
                                  <td className="player-name">
                                    <a href={`https://google.com/search?q=${encodeURIComponent(pick.player.name)}`} target="_blank" rel="noopener noreferrer" className={isEliminated ? 'player-eliminated' : ''}>{pick.player.name}</a>
                                    {isEliminated && <span className="eliminated-badge">❌</span>}
                                  </td>
                                  <td><span className={`tour-badge ${pick.player.tour.toLowerCase()}`}>{pick.player.tour}</span></td>
                                  <td>#{pick.player.ranking}</td>
                                  <td>{pick.costCredits} cr</td>
                                  {isCommissioner && (
                                    <td>
                                      <button className="btn btn-tiny btn-danger" onClick={() => handleUndoPick(pick._id)}>
                                        Undo
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                      <div className="team-total">
                        Total: {stats.spent} / {league.draftBudget || 175} credits spent
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Draft Log */}
        {draftPicks.length > 0 && (
          <div className="draft-log-section">
            <h3>📜 Draft Log</h3>
            <div className="draft-log">
              {draftPicks.map(pick => (
                <div key={pick._id} className="draft-log-entry">
                  <span className="pick-number">#{pick.pickNumber}</span>
                  <span className="pick-manager">
                    {pick.manager.firstName || pick.manager.username}
                  </span>
                  <span className="pick-arrow">→</span>
                  <span className="pick-player"><a href={`https://google.com/search?q=${encodeURIComponent(pick.player.name)}`} target="_blank" rel="noopener noreferrer">{pick.player.name}</a></span>
                  <span className={`tour-badge ${pick.player.tour.toLowerCase()}`}>{pick.player.tour}</span>
                  <span className="pick-cost">{pick.costCredits} cr</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Standings */}
        {standings.length > 0 && (
          <div className="standings-section">
            <h3>🏆 Standings</h3>
            <table className="standings-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Manager</th>
                  <th>Players</th>
                  <th>Points</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((entry, idx) => (
                  <tr key={entry.manager._id} className={entry.manager._id === user?._id ? 'standings-you' : ''}>
                    <td className="standings-rank">{idx + 1}</td>
                    <td>
                      {entry.manager.firstName || entry.manager.username}
                      {entry.manager._id === user?._id && <span className="you-badge">You</span>}
                    </td>
                    <td>{entry.players.length}</td>
                    <td className="standings-points">{entry.totalPoints}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Expandable player breakdown for each standing entry */}
            {standings.map((entry, idx) => (
              entry.players.length > 0 && entry.players.some(p => p.points > 0) && (
                <details key={entry.manager._id} className="standings-detail">
                  <summary>
                    #{idx + 1} {entry.manager.firstName || entry.manager.username} — {entry.totalPoints} pts breakdown
                  </summary>
                  <div className="standings-breakdown">
                    {[...entry.players]
                      .sort((a, b) => {
                        const aOut = (a.roundsLost || []).length > 0;
                        const bOut = (b.roundsLost || []).length > 0;
                        if (aOut !== bOut) return aOut ? 1 : -1;
                        return b.points - a.points;
                      })
                      .map(p => {
                        const isEliminated = (p.roundsLost || []).length > 0;
                        return (
                          <div key={p.player._id} className={`breakdown-row${isEliminated ? ' player-eliminated-row' : ''}`}>
                            <span className="breakdown-player">
                              <a href={`https://google.com/search?q=${encodeURIComponent(p.player.name)}`} target="_blank" rel="noopener noreferrer" className={isEliminated ? 'player-eliminated' : ''}>{p.player.name}</a>
                              {isEliminated && <span className="eliminated-badge">❌</span>}
                            </span>
                            <span className={`tour-badge ${p.player.tour.toLowerCase()}`}>{p.player.tour}</span>
                            <span className="breakdown-rounds">
                              {scoringModel === 'rounds-won'
                                ? [
                                    ...(p.roundsWon?.map(r => `W:${r}`) || []),
                                    ...(p.roundsLost?.map(r => `L:${r}`) || []),
                                  ].join(', ') || '—'
                                : (p.roundPositions?.map(rp => `${rp.round}:#${rp.position}`).join(', ') || '—')
                              }
                            </span>
                            <span className="breakdown-points">{p.points} pts</span>
                          </div>
                        );
                      })}
                  </div>
                </details>
              )
            ))}
          </div>
        )}

        {/* Commissioner: Enter Results */}
        {isCommissioner && (draftPicks.length > 0 || leagueHeroPicks.length > 0) && (
          <div className="results-entry-section">
            <h3>📝 Enter Tournament Results</h3>
            <p className="results-instructions">
              {scoringModel === 'rounds-won'
                ? 'Click to cycle each round: Win → Loss → clear.'
                : 'Enter each player\'s leaderboard position after each round. Clear a value to remove.'}
              {scoringTable && scoringModel === 'rounds-won' && (
                <span className="scoring-hint">
                  {' '}Points: {Object.entries(scoringTable).map(([r, p]) => `${r}=${p}`).join(', ')}
                </span>
              )}
            </p>
            <div className="results-table-wrapper">
              <table className="results-entry-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Tour</th>
                    {scoringRounds.map(r => (
                      <th key={r}>{r}</th>
                    ))}
                    <th>Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Build combined unique player list: heroes + regular picks
                    const heroPlayerIds = new Set(leagueHeroPicks.map(hp => hp.player._id));
                    const allPlayersMap = new Map();
                    leagueHeroPicks.forEach(hp => { if (!allPlayersMap.has(hp.player._id)) allPlayersMap.set(hp.player._id, hp.player); });
                    draftPicks.forEach(p => { if (!allPlayersMap.has(p.player._id)) allPlayersMap.set(p.player._id, p.player); });
                    return Array.from(allPlayersMap.values());
                  })()
                    .sort((a, b) => a.tour.localeCompare(b.tour) || a.ranking - b.ranking)
                    .map(player => {
                      const isHero = new Set(leagueHeroPicks.map(hp => hp.player._id)).has(player._id);
                      const playerResult = playerResults[player._id] || { roundsWon: [], roundPositions: [] };

                      // Compute points for display only
                      let pts = 0;
                      if (scoringModel === 'rounds-won' && scoringTable) {
                        pts = playerResult.roundsWon.reduce((sum, r) => sum + (scoringTable[r] || 0), 0);
                      } else if (scoringModel === 'position-by-round' && scoringTable) {
                        pts = playerResult.roundPositions.reduce((sum, { position }) => {
                          const bracket = scoringTable.find(b => position <= b.maxPosition);
                          return sum + (bracket ? bracket.points : 0);
                        }, 0);
                      }

                      return (
                        <tr key={player._id}>
                          <td className="player-name">
                            <a href={`https://google.com/search?q=${encodeURIComponent(player.name)}`} target="_blank" rel="noopener noreferrer">{player.name}</a>
                            {isHero && <span className="hero-badge-small" title="Hero pick">⚡</span>}
                          </td>
                          <td><span className={`tour-badge ${player.tour.toLowerCase()}`}>{player.tour}</span></td>
                          {scoringRounds.map(r => {
                            const loadKey = `${player._id}-${r}`;
                            const isLoading = resultLoading[loadKey];

                            if (scoringModel === 'rounds-won') {
                              const isWon  = playerResult.roundsWon.includes(r);
                              const isLost = (playerResult.roundsLost || []).includes(r);

                              // Disable rounds that come after a recorded loss
                              const lossIdx = scoringRounds.findIndex(sr => (playerResult.roundsLost || []).includes(sr));
                              const roundIdx = scoringRounds.indexOf(r);
                              const isAfterLoss = lossIdx >= 0 && roundIdx > lossIdx;

                              let label = '·';
                              let btnClass = 'round-btn';
                              let titleText = `Record ${r} win`;
                              if (isWon) {
                                label = 'W';
                                btnClass = 'round-btn round-won';
                                titleText = `Record ${r} loss`;
                              } else if (isLost) {
                                label = 'L';
                                btnClass = 'round-btn round-lost';
                                titleText = `Clear ${r} result`;
                              }
                              if (isLoading) btnClass += ' round-loading';

                              return (
                                <td key={r}>
                                  <button
                                    className={btnClass}
                                    onClick={() => handleToggleResult(player._id, r)}
                                    disabled={isLoading || isAfterLoss}
                                    title={isAfterLoss ? 'Player eliminated' : titleText}
                                  >
                                    {label}
                                  </button>
                                </td>
                              );
                            }

                            // position-by-round: number input
                            const entry = playerResult.roundPositions.find(e => e.round === r);
                            return (
                              <td key={r}>
                                <input
                                  type="number"
                                  className={`position-input${isLoading ? ' round-loading' : ''}`}
                                  min="1"
                                  placeholder="—"
                                  value={entry ? entry.position : ''}
                                  disabled={isLoading}
                                  onChange={() => {}} // controlled via onBlur
                                  onBlur={e => {
                                    const val = e.target.value.trim();
                                    if (val === String(entry?.position || '')) return; // no change
                                    handleSetPosition(player._id, r, val);
                                  }}
                                  title={`${player.name} position after ${r}`}
                                  style={{ width: '52px' }}
                                />
                              </td>
                            );
                          })}
                          <td className="result-points">{pts}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Delete League Confirm Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h4>Delete League?</h4>
            <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.5, margin: '8px 0 16px' }}>
              Are you sure you want to permanently delete <strong>{league.name}</strong>?<br />
              This cannot be undone.
            </p>
            {deleteError && (
              <p style={{ color: '#c0392b', fontSize: '0.88rem', marginBottom: '12px' }}>{deleteError}</p>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteLeague}>Delete League</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeagueDetail;
